import type { CustomReaction } from '@tribeplatform/gql-client/types'
import { noop } from '@tribeplatform/react-ui-kit/hooks'

import { type ReactionItem, ReactionTypes, ReactionErrors } from '../types.js'

import { convertToCustomReactions } from './converters.js'

export const getReactionType = (allowedReactions: string[]): ReactionTypes => {
  const hasCustomReaction = allowedReactions?.length > 0
  const isReactionsDisabled = allowedReactions?.length === 0
  const hasAllEmojis = allowedReactions === null

  if (hasCustomReaction) {
    return ReactionTypes.custom
  }

  if (isReactionsDisabled) {
    return ReactionTypes.none
  }

  if (hasAllEmojis) {
    return ReactionTypes.all
  }

  return ReactionTypes.none
}

export const getCustomReactions = (
  reactionType: ReactionTypes,
  reactionItems: ReactionItem[],
): CustomReaction[] => {
  switch (reactionType) {
    case ReactionTypes.all:
      return []
    case ReactionTypes.custom:
      return convertToCustomReactions(reactionItems)
    case ReactionTypes.none:
      return []
    default: {
      // Code should never reach the default case
      const exhaustiveCheck: never = reactionType
      noop(exhaustiveCheck)
      return []
    }
  }
}

export const getSingleChoiceReactions = (
  reactionType: ReactionTypes,
  singleChoice: boolean,
  reactionItems: ReactionItem[],
): string[] => {
  switch (reactionType) {
    case ReactionTypes.all: {
      if (singleChoice) {
        return null
      }

      return []
    }
    case ReactionTypes.custom: {
      if (singleChoice) {
        return reactionItems.map(({ key }) => key)
      }

      return []
    }
    case ReactionTypes.none:
      return []
    default: {
      // Code should never reach the default case
      const exhaustiveCheck: never = reactionType
      noop(exhaustiveCheck)
      return []
    }
  }
}

export const getAllowedReactions = (
  reactionType: ReactionTypes,
  reactionItems: ReactionItem[],
): string[] => {
  switch (reactionType) {
    case ReactionTypes.all:
      return null
    case ReactionTypes.custom: {
      return reactionItems.map(({ key }) => key)
    }
    case ReactionTypes.none:
      return []
    default: {
      // Code should never reach the default case
      const exhaustiveCheck: never = reactionType
      noop(exhaustiveCheck)
      return []
    }
  }
}

export const validateReactions = (
  customReactions: CustomReaction[],
  allowedReactions: string[],
  singleChoiceReactions: string[],
): ReactionErrors => {
  const hasDuplicateName = hasDuplicate(customReactions.map(({ name }) => name))
  const hasEmptyName = customReactions.some(({ name }) => name === '')
  const hasDuplicateIcon = hasDuplicate(
    customReactions.map(({ glyphId }) => glyphId),
  )
  const hasEmptyIcon = customReactions.some(({ glyphId }) => !glyphId)
  const hasDuplicateKey =
    (!!allowedReactions && hasDuplicate(allowedReactions)) ||
    (!!singleChoiceReactions && hasDuplicate(singleChoiceReactions))

  if (hasDuplicateName || hasEmptyName) {
    return ReactionErrors.name
  }

  if (hasDuplicateIcon || hasEmptyIcon) {
    return ReactionErrors.icon
  }

  if (hasDuplicateKey) {
    return ReactionErrors.key
  }

  return ReactionErrors.none
}

const hasDuplicate = (items: Array<unknown>): boolean => {
  return items.length !== new Set(items).size
}
